import { useCallback, useEffect, useState } from "react";

/**
 * The key pad, in both the places an agent needs one.
 *
 * `variant="dial"` — idle, on the presence surface: type a number and place a
 * call. `variant="dtmf"` — mid-call, inside the dark cockpit: every press is a
 * tone sent down the live call so the agent can work an IVR.
 *
 * One component rather than two because the layout, the key labels, the
 * keyboard handling and the touch targets are identical; only what a press
 * *means* differs. Two copies of a phone key pad would drift, and the drift
 * would land on the half nobody looks at.
 */

export type DialpadVariant = "dial" | "dtmf";

export interface DialpadProps {
  variant: DialpadVariant;
  /** dial: place the call. Not called when the number is unusable. */
  onCall?: (number: string) => void;
  /** dtmf: one tone per press. */
  onTone?: (tone: string) => void;
  /** dial: false disables Call and shows `blockedReason` instead of the hint. */
  canDial?: boolean;
  blockedReason?: string | null;
  /** dial: the number the far end will see. Shown so the agent knows. */
  callerId?: string | null;
  /** dial: true while an INVITE is in flight. */
  busy?: boolean;
}

// Letters are not decoration: agents read numbers off scripts and business
// cards that spell them ("call 1-800-FLOWERS"), and a pad without letters makes
// that a translation exercise. Also the standard phone layout — muscle memory.
const KEYS: { digit: string; letters?: string }[] = [
  { digit: "1" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*" },
  { digit: "0", letters: "+" },
  { digit: "#" },
];

/** Digits only, capped at a length no real NANPA number exceeds. */
function digitsOf(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 11);
}

/**
 * Format as the agent types: (216) 376-9916, or 1 (216) 376-9916.
 *
 * Formatting only kicks in once the shape is unambiguous. Reformatting on
 * every keystroke from the first digit makes the caret jump and turns a typo
 * into a re-entry, which is exactly the moment an agent misdials.
 */
function formatAsTyped(digits: string): string {
  const d = digits.startsWith("1") && digits.length > 1 ? digits.slice(1) : digits;
  const lead = digits.startsWith("1") && digits.length > 1 ? "1 " : "";
  if (d.length <= 3) return lead + d;
  if (d.length <= 6) return `${lead}(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `${lead}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

/**
 * Is this dialable? 10 digits, or 11 starting with 1 — the same NANPA rule the
 * dialplan enforces (`ictbroadcast-agent-outbound`). Checked here purely so the
 * Call button can be honest about it; the PBX re-checks and refuses anything
 * else regardless of what this returns.
 */
export function isDialable(digits: string): boolean {
  if (digits.length === 10) return true;
  return digits.length === 11 && digits.startsWith("1");
}

export function Dialpad({
  variant,
  onCall,
  onTone,
  canDial = true,
  blockedReason,
  callerId,
  busy = false,
}: DialpadProps) {
  const dark = variant === "dtmf";
  const [entry, setEntry] = useState("");

  const digits = digitsOf(entry);
  const ready = isDialable(digits) && canDial && !busy;

  const press = useCallback(
    (key: string) => {
      if (variant === "dtmf") {
        onTone?.(key);
        // Mid-call the pad is a tone generator, not a composer: the display
        // echoes what was sent so an agent can see they hit 2 and not 5.
        setEntry((prev) => (prev + key).slice(-20));
        return;
      }
      // * and # are meaningless in a number being composed for the PSTN, and
      // the dialplan strips them anyway — leaving them out of the entry keeps
      // the display honest about what will actually be dialled.
      if (/[0-9]/.test(key)) setEntry((prev) => digitsOf(prev + key));
    },
    [variant, onTone],
  );

  const backspace = useCallback(() => setEntry((prev) => prev.slice(0, -1)), []);

  // The number deliberately SURVIVES the press. A call that gets refused —
  // busy, declined, out of service — leaves the agent looking at the pad, and
  // wiping the field meant retyping all ten digits to try again. When the call
  // does connect, the console swaps this whole surface for the in-call
  // cockpit, so the pad unmounts and comes back empty on its own.
  const submit = useCallback(() => {
    if (!ready) return;
    onCall?.(digits);
  }, [ready, onCall, digits]);

  // Physical keyboard. Agents type numbers far faster than they tap them, and
  // a pad that only accepts clicks is slower than the phone it replaced.
  // Scoped to this component's own subtree via the input/button handlers rather
  // than a document listener, so typing in any other field is untouched.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (variant === "dial") submit();
        return;
      }
      if (e.key === "Backspace") {
        // Handled here rather than natively because the field shows a
        // *formatted* number: letting the browser delete a ")" would leave the
        // digits untouched and the key look broken. Mid-call there is nothing
        // to un-send, so it's swallowed instead.
        e.preventDefault();
        if (variant === "dial") backspace();
        return;
      }
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        press(e.key);
      }
    },
    [variant, submit, press, backspace],
  );

  // Fresh pad each time it opens mid-call, so a previous call's tones aren't
  // sitting in the display.
  useEffect(() => {
    if (variant === "dtmf") setEntry("");
  }, [variant]);

  const keyClass = dark
    ? "bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white/40"
    : "bg-gray-50 text-gray-900 ring-1 ring-gray-200 hover:bg-gray-100 focus-visible:ring-brand";

  return (
    <div className={dark ? "" : "mt-4"}>
      {/* --- Display -------------------------------------------------------- */}
      <div className="relative">
        <label htmlFor={`dialpad-${variant}`} className="sr-only">
          {variant === "dial" ? "Number to call" : "Tones sent"}
        </label>
        <input
          id={`dialpad-${variant}`}
          value={variant === "dial" ? formatAsTyped(digits) : entry}
          readOnly={variant === "dtmf"}
          onChange={(e) => setEntry(digitsOf(e.target.value))}
          onKeyDown={onKeyDown}
          inputMode="tel"
          autoComplete="off"
          placeholder={variant === "dial" ? "Enter a number" : "Tones you send appear here"}
          aria-live={variant === "dtmf" ? "polite" : undefined}
          className={`w-full rounded-xl px-4 py-3.5 pr-12 text-center font-mono text-2xl tabular-nums tracking-tight focus:outline-none ${
            dark
              ? "border border-white/15 bg-slate-950/60 text-white placeholder:text-white/25 placeholder:text-base focus:border-white/40"
              : "border border-gray-200 bg-white text-gray-900 placeholder:text-gray-300 placeholder:text-base focus:border-brand"
          }`}
        />
        {entry.length > 0 && variant === "dial" && (
          <button
            type="button"
            onClick={backspace}
            aria-label="Delete last digit"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2 text-lg text-gray-400 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            ⌫
          </button>
        )}
      </div>

      {/* --- Keys ----------------------------------------------------------- */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {KEYS.map(({ digit, letters }) => (
          <button
            key={digit}
            type="button"
            onClick={() => press(digit)}
            onKeyDown={onKeyDown}
            // Labelled explicitly so a screen reader announces "5", not
            // "5 JKL" — the letters are a visual aid for reading numbers off
            // a page, not part of what the key is.
            aria-label={digit}
            className={`rounded-xl py-3 transition focus:outline-none focus-visible:ring-2 ${keyClass}`}
          >
            <span className="block text-2xl font-medium leading-none tabular-nums">
              {digit}
            </span>
            <span
              className={`mt-1 block text-[10px] font-semibold uppercase tracking-widest ${
                dark ? "text-white/35" : "text-gray-400"
              }`}
            >
              {/* Non-breaking space keeps every key the same height whether or
                  not it carries letters — a ragged pad is harder to hit. */}
              {letters || " "}
            </span>
          </button>
        ))}
      </div>

      {/* --- Call action (dial only) ---------------------------------------- */}
      {variant === "dial" && (
        <>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            className="mt-3 w-full rounded-xl bg-emerald-600 px-6 py-4 text-lg font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300"
          >
            {busy ? "Calling…" : "Call"}
          </button>
          <p className="mt-2 text-center text-xs text-gray-500">
            {blockedReason
              ? blockedReason
              : callerId
                ? `They'll see ${formatAsTyped(digitsOf(callerId))}`
                : "Enter a 10-digit US or Canadian number."}
          </p>
        </>
      )}
    </div>
  );
}
